import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Claim } from 'src/app/core/models/claim';
import { Contract } from 'src/app/core/models/contract';
import { User } from 'src/app/core/models/user.model';
import { Action } from 'src/app/core/models/action.model';
import { ContractService } from 'src/app/core/services/contract.service';
import { SignedContractService } from 'src/app/core/services/signed_contract.service';
import { UserService } from 'src/app/core/services/user.service';
import { ActionPollingService } from 'src/app/modules/client/pages/guide/services/action-polling.service';
import { Subscription } from 'rxjs';
import { EventService } from 'src/app/core/services/event.service';
import { EventTypes } from 'src/app/core/models/events.model';
import { ActivatedRoute } from '@angular/router';
import { AgentClientsService } from 'src/app/core/services/agent-clients.service';
import { VideoConfig, VideoItemConfig } from 'src/app/core/models/contract';
import { MatDialog } from '@angular/material/dialog';
import { VideoModalComponent, VideoModalData, VideoModalResult } from './video-modal/video-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { signatureErrorMessage } from './signature-error';

type GuideSignatureState = 'PENDING' | 'PROCESSING_SYNC' | 'DONE' | 'FAILED';

@Component({
  selector: 'app-eletronic-signature-modal',
  templateUrl: './eletronic-signature-modal.component.html',
  styleUrls: ['./eletronic-signature-modal.component.scss'],
})
export class EletronicSignatureModalComponent implements OnInit, OnDestroy {
  @Input() contractId?: string;
  @Input() claimId?: string;
  @Input() componentView = false;
  @Input() action?: Action; // Para suporte ao Guide com polling

  @Output() onComplete = new EventEmitter();

  @ViewChild('signatureContainer') signatureContainer?: ElementRef<HTMLDivElement>;

  template = 'pdf';

  date = new Date();
  user?: User;

  contract?: Contract;
  claim?: Claim;
  url?: string;

  canProceedToSign: boolean = false;

  loading = false;
  // signature flow state passed to child signature-pad
  signatureLoading = false;
  signatureSuccess = false;
  signatureError: string | null = null;

  // Video step
  videoConfig?: VideoConfig;
  videoItems: VideoItemConfig[] = [];
  videoCompleted: boolean[] = [];
  videoProgress: number[] = [];
  allVideosCompleted = false;
  cameFromChoice = false;

  // Estado do componente no contexto do Guide
  currentState: GuideSignatureState = 'PENDING';

  // Polling
  pollingSubscription?: Subscription;
  processingMessage = 'Processando assinatura...';
  processingProgress = 0;

  constructor(
    private userService: UserService,
    private contractService: ContractService,
    private signedContractService: SignedContractService,
    private actionPollingService: ActionPollingService,
    private eventService: EventService,
    private activatedRoute: ActivatedRoute,
    private agentClientsService: AgentClientsService,
    private dialog: MatDialog,
    private translate: TranslateService,
    public dialogRef: MatDialogRef<EletronicSignatureModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { contract_id: string; claim_id: string }
  ) {}

  get isV2(): boolean {
    return !!this.contract?.configuration?.multiple_explanations;
  }

  get isChoiceMode(): boolean {
    return !!this.videoConfig?.choice_mode;
  }

  get canProceedFromChoice(): boolean {
    return this.allVideosCompleted || this.canProceedToSign;
  }

  async ngOnInit() {
    if (!!this.claimId && !!this.contractId && this.componentView) {
      this.data.claim_id = this.claimId;
      this.data.contract_id = this.contractId;
    }

    console.log('[GUIDE_SIGNATURE] Component initialized', {
      contractId: this.data.contract_id,
      claimId: this.data.claim_id,
      componentView: this.componentView,
      hasAction: !!this.action,
      actionStatus: this.action?.ac_status,
    });

    // Se tem action e está no Guide, verifica status inicial
    if (this.componentView && this.action) {
      await this.checkInitialState();

      // Se já está completo, não precisa carregar o contrato
      if (this.currentState === 'DONE') {
        return;
      }

      // Se está em polling, não precisa carregar o contrato
      if (this.currentState === 'PROCESSING_SYNC') {
        return;
      }
    }

    this.contract = await this.contractService.get(this.data.contract_id);

    console.log('[GUIDE_SIGNATURE] Contract loaded', {
      contractId: this.contract?.id,
      hasExplanation: !!this.contract?.configuration?.explanation_text,
      hasVideo: !!this.contract?.configuration?.video,
    });

    if (!this.contract) {
      console.error('[GUIDE_SIGNATURE] Contract not found', {
        contractId: this.data.contract_id,
      });
    }

    // Video step: show if configured AND not skipped by impersonate
    this.videoConfig = this.contract?.configuration?.video;
    const isImpersonate = this.agentClientsService.isAgentSession();
    const skipVideo = isImpersonate && this.videoConfig?.skip_on_impersonate;

    if (this.videoConfig && !skipVideo) {
      // Build video items: support both videos[] array and legacy single url
      if (this.videoConfig.videos?.length) {
        this.videoItems = this.videoConfig.videos;
      } else if (this.videoConfig.url) {
        this.videoItems = [{ title: 'Video', url: this.videoConfig.url, unskippable: this.videoConfig.unskippable }];
      }

      if (this.videoItems.length > 0) {
        this.videoCompleted = this.videoItems.map((_, i) => {
          const saved = this.loadVideoProgress(i);
          return saved?.completed ?? false;
        });
        this.videoProgress = this.videoItems.map((_, i) => {
          const saved = this.loadVideoProgress(i);
          return saved?.progress ?? 0;
        });
        this.allVideosCompleted = this.videoCompleted.every(v => v);

        if (this.videoConfig.choice_mode) {
          this.changeTemplate('choice');
        } else if (!this.allVideosCompleted) {
          this.changeTemplate('video');
        }
      }
    } else if (this.isV2) {
      if (this.getExplanation('PRE_DOCS')) {
        this.changeTemplate('explanation');
      }
    } else {
      if (this.contract.configuration.explanation_text) {
        this.changeTemplate('explanation');
      }
    }

    this.url = await this.contractService.getContractDocumentURLById(
      this.contract.id!
    );

    const u = await this.userService.getCurrentUser();
    if (!u) {
      console.error('[GUIDE_SIGNATURE] User not found');
      throw new Error('This screen requires a user.');
    }
    this.user = u;

    console.log('Lista de documentos carregada');
    await this.sendDocumentEvent();

  }

  ngOnDestroy(): void {
    console.log('[GUIDE_SIGNATURE] Component destroyed', {
      currentState: this.currentState,
    });

    this.pollingSubscription?.unsubscribe();
  }

  onVideoCompleted(): void {
    this.allVideosCompleted = this.videoCompleted.every(v => v);
    console.log('[GUIDE_SIGNATURE] Video completed check', { allDone: this.allVideosCompleted });
  }

  openVideoModal(index: number): void {
    const video = this.videoItems[index];
    const storageKey = `cp_video_progress_${this.data.claim_id}_${index}`;
    const ref = this.dialog.open(VideoModalComponent, {
      data: {
        video,
        controls: this.videoConfig?.controls,
        storageKey,
        claimId: this.data.claim_id,
        videoIndex: index,
      } as VideoModalData,
      width: '90vw',
      maxWidth: '800px',
      panelClass: 'video-modal-panel',
      disableClose: true,
    });

    ref.afterClosed().subscribe((result: VideoModalResult | undefined) => {
      if (result) {
        this.videoProgress[index] = result.progress;
        if (result.completed) {
          this.videoCompleted[index] = true;
          this.videoProgress[index] = 100;
        }
        this.onVideoCompleted();
      }
    });
  }

  proceedFromVideo(): void {
    this.eventService.pushNew(EventTypes.VIDEO_CONTINUE_CLICK, {
      claim_id: this.data.claim_id,
    }).catch(() => {});

    if (this.cameFromChoice) {
      this.changeTemplate('choice');
      return;
    }

    if (this.isV2) {
      if (this.getExplanation('PRE_DOCS')) {
        this.changeTemplate('explanation');
      } else {
        this.changeTemplate('pdf');
      }
    } else {
      if (this.contract?.configuration?.explanation_text) {
        this.changeTemplate('explanation');
      } else {
        this.changeTemplate('pdf');
      }
    }
  }

  goToVideoChoice(): void {
    this.cameFromChoice = true;
    this.changeTemplate('video');
  }

  goToDocumentChoice(): void {
    this.cameFromChoice = true;
    this.changeTemplate('pdf');
  }

  backToChoice(): void {
    this.changeTemplate('choice');
  }

  proceedFromChoice(): void {
    if (this.getExplanation('PRE_BRB')) {
      this.changeTemplate('explanation-pre-brb');
    } else {
      this.changeTemplate('terms-required-BRB');
    }
  }

  getExplanation(type: 'PRE_DOCS' | 'PRE_BRB' | 'PRE_SIGNATURE'): string | undefined {
    return this.contract?.configuration?.multiple_explanations?.[type];
  }

  goToBrb() {
    if (this.getExplanation('PRE_BRB')) {
      this.changeTemplate('explanation-pre-brb');
    } else {
      this.changeTemplate('terms-required-BRB');
    }
  }

  /**
   * Verifica o estado inicial da action e decide o que fazer
   */
  private async checkInitialState(): Promise<void> {
    if (!this.action) {
      this.currentState = 'PENDING';
      return;
    }

    const status = this.action.ac_status;

    console.log('[GUIDE_SIGNATURE] Checking initial state', {
      actionId: this.action.action_id,
      status,
    });

    switch (status) {
      case 'SIGNED':
        // ✅ Já assinado - emite evento imediatamente
        console.log('[GUIDE_SIGNATURE] Already signed, emitting event', {
          actionId: this.action.action_id,
        });
        this.currentState = 'DONE';
        this.template = 'processing'; // Template de processamento
        this.onComplete.emit();
        break;

      case 'PROCESSING':
        // ⏳ Em processamento - inicia polling
        console.log('[GUIDE_SIGNATURE] In processing, starting polling', {
          actionId: this.action.action_id,
        });
        this.currentState = 'PROCESSING_SYNC';
        this.template = 'processing'; // Template de processamento
        this.startPolling();
        break;

      case 'PENDING':
      case 'FAILED':
      case null:
      case undefined:
      default:
        // ⚠️ Pendente - mostra formulário de assinatura
        console.log('[GUIDE_SIGNATURE] Pending, showing signature form', {
          actionId: this.action.action_id,
          status,
        });
        this.currentState = 'PENDING';
        break;
    }
  }

  /**
   * Inicia polling exponencial para verificar status da assinatura
   */
  private startPolling(): void {
    if (!this.action?.action_id) {
      console.error('[GUIDE_SIGNATURE] Cannot start polling - no action_id');
      return;
    }

    console.log('[GUIDE_SIGNATURE] Starting polling', {
      actionId: this.action.action_id,
    });

    this.processingProgress = 0;
    this.processingMessage = 'Processando assinatura...';

    this.pollingSubscription = this.actionPollingService.poll({
      actionId: this.action.action_id,
      claimId: this.action.claim_id || undefined,
      actionType: 'SIGNATURE',
      initialDelay: 2000,      // 2s
      maxDelay: 15000,         // 15s max (assinatura é mais rápida)
      backoffMultiplier: 1.5,
      maxAttempts: 10          // ~3-4 minutos total
    }).subscribe({
      next: (result) => {
        console.log('[GUIDE_SIGNATURE] Polling result received', {
          actionId: this.action!.action_id,
          status: result.status,
          completed: result.completed,
          attempt: result.attempt,
        });

        // Atualiza UI
        this.updateProcessingUI(result.attempt);

        // Atualiza action local
        this.action = result.action;

        if (result.completed && result.status === 'SIGNED') {
          // ✅ Assinatura completa!
          console.log('[GUIDE_SIGNATURE] Signature completed successfully', {
            actionId: this.action!.action_id,
            totalAttempts: result.attempt,
          });
          this.currentState = 'DONE';
          this.processingProgress = 100;
          this.processingMessage = 'Assinatura concluída!';
          this.signatureSuccess = true;

          // Aguarda 1s para mostrar mensagem de sucesso
          setTimeout(() => {
            this.onComplete.emit();
          }, 1000);
        } else if (result.status === 'FAILED' || result.status === 'REJECTED') {
          // ❌ Assinatura falhou
          console.log('[GUIDE_SIGNATURE] Signature failed', {
            actionId: this.action!.action_id,
            status: result.status,
          });
          this.currentState = 'FAILED';
          this.signatureError = 'Assinatura não pôde ser processada. Por favor, tente novamente.';
          this.changeTemplate('pdf'); // Volta para template de assinatura
        }
      },
      error: (err) => {
        // ❌ Erro no polling ou timeout
        console.error('[GUIDE_SIGNATURE] Polling error', {
          actionId: this.action!.action_id,
          error: err instanceof Error ? err.message : String(err),
        });
        this.currentState = 'FAILED';
        this.signatureError = 'Não conseguimos verificar o status da assinatura. Por favor, recarregue a página.';
        this.changeTemplate('pdf'); // Volta para template de assinatura
      }
    });
  }

  /**
   * Atualiza UI da tela de processamento
   */
  private updateProcessingUI(attempt: number): void {
    // Incrementa progresso gradualmente (máx 90% durante polling)
    this.processingProgress = Math.min(this.processingProgress + 10, 90);

    // Mensagens contextuais
    if (this.processingProgress < 40) {
      this.processingMessage = 'Processando assinatura...';
    } else if (this.processingProgress < 70) {
      this.processingMessage = 'Validando documento...';
    } else {
      this.processingMessage = 'Quase lá...';
    }
  }

  changeTemplate(template: string) {
    this.template = template;
    this.scrollToTop();
  }

  /**
   * Rolagem automática para o topo do container ao mudar de página/template
   */
  private scrollToTop(): void {
    // Pequeno delay para garantir que o DOM foi atualizado
    setTimeout(() => {
      // Tenta scrollar no container referenciado
      if (this.signatureContainer?.nativeElement) {
        this.signatureContainer.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
      // Fallback: scrollar no window
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }, 0);
  }

  docBundleValid(event: boolean) {
    this.canProceedToSign = event;
  }

  userAgreeWithTerms(event: boolean): any {
    if(event && event === true){
      this.eventService.pushNew(EventTypes.VIDEO_ACCEPT_CLICK, {
        claim_id: this.data.claim_id,
      }).catch(() => {});

      if (this.getExplanation('PRE_SIGNATURE')) {
        this.changeTemplate('explanation-pre-signature');
      } else {
        this.changeTemplate('assignature');
      }
    }
  }

  async sendDraw(event: any) {
    // set both modal and child signature state
    this.loading = true;
    this.signatureLoading = true;
    this.signatureSuccess = false;
    this.signatureError = null;

    console.log('[GUIDE_SIGNATURE] Submitting signature', {
      contractId: this.contract?.id,
      claimId: this.data.claim_id,
    });

    try {
      const response = await this.signedContractService.doSign(
        this.contract!.id!,
        this.data.claim_id,
        event,
        this.userService.getFingerprint(),
        '000.000.000.000'
      );

      console.log('[GUIDE_SIGNATURE] Signature submitted successfully', {
        response,
        status: response?.message?.status
      });

      this.signatureSuccess = true;

      // Verifica o status da resposta
      const status = response?.message?.status;

      if (status === 'signed document') {
        // ✅ Documento assinado imediatamente - completa sem polling
        console.log('[GUIDE_SIGNATURE] Document signed immediately - completing without polling');
        this.currentState = 'DONE';

        if (this.componentView && this.action?.action_id) {
          // No Guide, emite evento de conclusão
          this.onComplete.emit();
        } else {
          // Modo normal (modal) - mostra tela de sucesso
          this.changeTemplate('sucess');
        }
      } else {
        // ⏳ Precisa de polling para verificar status
        console.log('[GUIDE_SIGNATURE] Document requires processing - starting polling', {
          status
        });

        if (this.componentView && this.action?.action_id) {
          console.log('[GUIDE_SIGNATURE] Starting polling after signature');
          this.currentState = 'PROCESSING_SYNC';
          this.changeTemplate('processing');
          this.startPolling();
        } else {
          // Modo normal (modal) - mostra tela de sucesso
          this.changeTemplate('sucess');
        }
      }
    } catch (error) {
      console.error('[GUIDE_SIGNATURE] Error submitting signature', {
        error: error instanceof Error ? error.message : String(error),
      });

      // prazo interno vencido (CPK-5212) → mensagem traduzida; senão, a do servidor
      this.signatureError = signatureErrorMessage(error, (key) => this.translate.instant(key));
      this.currentState = 'FAILED';
    } finally {
      this.loading = false;
      this.signatureLoading = false;
    }
  }

  close() {
    console.log('[GUIDE_SIGNATURE] Close called', {
      componentView: this.componentView,
      currentState: this.currentState,
    });

    if (this.componentView) {
      this.onComplete.next(true);
    } else {
      this.dialogRef.close();
    }
  }

  private async sendDocumentEvent(): Promise<any> {

    const claimIdUrlParam = this.activatedRoute.snapshot.paramMap.get('claimId');;
    const documentEvent: EventTypes = EventTypes.SIGNATURE_DOC_LIST_LOAD_PAGE;

    try{
      await this.eventService.sendEventWithCaseId(documentEvent, claimIdUrlParam);
    }catch(error){
      console.error(error);
    }

  }

  private loadVideoProgress(index: number): { progress: number; currentTime: number; completed: boolean } | null {
    const key = `cp_video_progress_${this.data.claim_id}_${index}`;
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }
}
